import React, { useEffect, useState } from 'react';

import pluralize from 'pluralize';

import { Box, Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import CopyableField from '~/components/Common/CopyableField';

import { ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { getLuxLoyaltyProgramConfig } from '~/services/LuxLoyaltyService';
import { formatDateSlashes, formatDateSlashesWithClock } from '~/services/TimeService';

import isProd from '~/utils/isProd';
import { getLuxLoyaltyTierDisplayName, getMembershipYearInfo } from '~/utils/luxLoyalty';
import {
  type ProgramConfigTier,
  canShowTierProgressionRetain,
  canShowTierProgressionUpgrade,
  canToggleTierProgressionView,
  getMembershipYearContext,
  getNextLuxLoyaltyTier,
  hasTierProgressionThresholdData,
  resolveTierProgressionThresholds,
} from '~/utils/luxLoyaltyTierProgression';
import { formatDecimal } from '~/utils/numberFormatters';
import { titleCase } from '~/utils/stringUtils';

import LuxLoyaltyAccountChip from './LuxLoyaltyAccountChip';
import LuxLoyaltyAdjustPointsModal from './LuxLoyaltyAdjustPointsModal';
import LuxLoyaltyChangeTierModal from './LuxLoyaltyChangeTierModal';
import RegionSwitcher from './RegionSwitcher';

const UPGRADEABLE_TIERS: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold', 'platinum'];

interface Props {
  account: App.LuxLoyaltyAccount;
  onAccountUpdated?: () => void;
}

interface KpiTileProps {
  label: string;
  value: React.ReactNode;
}

/**
 * Metric tile matching the StatCard pattern used across admin dashboards
 * (e.g. Extranet Approvals).
 */
function renderKpiTile({ label, value }: KpiTileProps) {
  return (
    <Paper key={label} variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2, borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5}>
        {value}
      </Typography>
    </Paper>
  );
}

function renderOverviewSection(title: string, children: React.ReactNode, sx?: SxProps<Theme>) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Typography
        variant="overline"
        sx={{ letterSpacing: 1, color: 'text.secondary', display: 'block', mb: 2, fontWeight: 700 }}
      >
        {title}
      </Typography>
      <Stack spacing={2} sx={{ flex: 1 }}>
        {children}
      </Stack>
    </Paper>
  );
}

function renderDetailField(label: string, value: React.ReactNode) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body1">{value}</Typography>
      ) : (
        value
      )}
    </Box>
  );
}

export default function LuxLoyaltyAccountOverview({ account, onAccountUpdated }: Props) {
  const formattedStatusCreditsName = titleCase(pluralize(LUX_LOYALTY_PROGRAM_CONFIG.statusCreditsUnit));
  const formattedCurrencyName = titleCase(pluralize(LUX_LOYALTY_PROGRAM_CONFIG.pointsUnit));
  const { user } = useCurrentUser();
  const [changeTierModalOpen, setChangeTierModalOpen] = useState(false);
  const canChangeTier = !isProd() || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);
  const [adjustPointsModalOpen, setAdjustPointsModalOpen] = useState(false);
  const canAdjustPoints = !isProd() || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);
  const [showUpgradeView, setShowUpgradeView] = useState(false);
  const [programConfigTiers, setProgramConfigTiers] = useState<ProgramConfigTier[]>();

  useEffect(() => {
    let cancelled = false;

    getLuxLoyaltyProgramConfig(account.region).then((config) => {
      if (!cancelled && config?.tiers) {
        setProgramConfigTiers(config.tiers);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [account.region]);

  const membershipYearEndDate = account.membershipYearEndDate;
  const membershipYearInfo =
    account.createdAt && membershipYearEndDate ? getMembershipYearInfo(account.createdAt, membershipYearEndDate) : null;
  const membershipYearContext = getMembershipYearContext(
    account.statusReviewDate,
    membershipYearEndDate,
    account.createdAt,
  );
  const canShowRetain = canShowTierProgressionRetain(account.tier, membershipYearContext);
  const canShowUpgrade = canShowTierProgressionUpgrade(account.tier);
  const nextTier = canShowUpgrade ? getNextLuxLoyaltyTier(account.tier) : undefined;
  const currentTierName = getLuxLoyaltyTierDisplayName(account.tier);
  const nextTierName = nextTier ? getLuxLoyaltyTierDisplayName(nextTier) : undefined;
  const canToggleView = canToggleTierProgressionView(canShowRetain, canShowUpgrade);
  const showRetainFields = canToggleView ? !showUpgradeView : canShowRetain;
  const showUpgradeFields = canToggleView ? showUpgradeView : canShowUpgrade;
  const retainThresholds = resolveTierProgressionThresholds(account, 'retain', programConfigTiers);
  const upgradeThresholds = resolveTierProgressionThresholds(account, 'upgrade', programConfigTiers);
  const hasTierProgression =
    (showRetainFields && hasTierProgressionThresholdData(retainThresholds)) ||
    (showUpgradeFields && hasTierProgressionThresholdData(upgradeThresholds));

  return (
    <Stack spacing={3}>
      <LuxLoyaltyAccountChip luxLoyaltyAccount={account} size="medium" fullWidth />

      {/* KPI tiles */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {renderKpiTile({
          label: formattedCurrencyName,
          value: formatDecimal(account.points),
        })}
        {renderKpiTile({
          label: formattedStatusCreditsName,
          value: formatDecimal(account.statusCredits),
        })}
        {renderKpiTile({ label: 'Number of Nights', value: account.numberOfNights })}
      </Stack>

      {canAdjustPoints && (
        <Box>
          <Button variant="contained" onClick={() => setAdjustPointsModalOpen(true)}>
            Adjust Points
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2.5,
          alignItems: 'stretch',
        }}
      >
        {renderOverviewSection(
          'Account Details',
          <>
            {account.accountNumber
              ? renderDetailField(
                  `${LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} LE Member ID`,
                  <CopyableField variant="body1" value={account.accountNumber} />,
                )
              : null}
            {renderDetailField(
              `${LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} External Provider ID`,
              <CopyableField variant="body1" value={account.ibsMemberId} />,
            )}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tier
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="body1">{getLuxLoyaltyTierDisplayName(account.tier)}</Typography>
                {canChangeTier && UPGRADEABLE_TIERS.includes(account.tier) && (
                  <Button variant="contained" onClick={() => setChangeTierModalOpen(true)}>
                    Change Tier
                  </Button>
                )}
              </Stack>
            </Box>
            {renderDetailField('Region', account.region)}
            <RegionSwitcher account={account} onRegionUpdated={onAccountUpdated} />
          </>,
        )}

        {renderOverviewSection(
          'Membership period',
          <>
            {membershipYearInfo
              ? renderDetailField(
                  'Membership year',
                  <>
                    <Typography variant="body1" fontWeight={600}>
                      Year {membershipYearInfo.yearNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {formatDateSlashes(membershipYearInfo.startDate)} –{' '}
                      {formatDateSlashes(membershipYearInfo.endDate)}
                    </Typography>
                  </>,
                )
              : null}
            {membershipYearEndDate
              ? renderDetailField('Membership year end date', formatDateSlashes(membershipYearEndDate))
              : null}
            {account.statusReviewDate
              ? renderDetailField('Tier review date', formatDateSlashes(account.statusReviewDate))
              : null}
            {!!account.createdAt && renderDetailField('Account created', formatDateSlashesWithClock(account.createdAt))}
          </>,
        )}

        {hasTierProgression &&
          renderOverviewSection(
            'Tier progression',
            <>
              {canToggleView && nextTierName ? (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={showUpgradeView ? 'upgrade' : 'retain'}
                  onChange={(_, value) => {
                    if (value) {
                      setShowUpgradeView(value === 'upgrade');
                    }
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  <ToggleButton value="retain">Retain {currentTierName}</ToggleButton>
                  <ToggleButton value="upgrade">Unlock {nextTierName}</ToggleButton>
                </ToggleButtonGroup>
              ) : null}
              {showRetainFields &&
                retainThresholds.statusCredits > 0 &&
                renderDetailField(
                  `${formattedStatusCreditsName} required for tier retention`,
                  retainThresholds.statusCredits,
                )}
              {showUpgradeFields &&
                upgradeThresholds.statusCredits > 0 &&
                renderDetailField(
                  `${formattedStatusCreditsName} required for tier upgrade`,
                  upgradeThresholds.statusCredits,
                )}
              {showRetainFields &&
                retainThresholds.nights > 0 &&
                renderDetailField('Number of nights required for tier retention', retainThresholds.nights)}
              {showUpgradeFields &&
                upgradeThresholds.nights > 0 &&
                renderDetailField('Number of nights required for tier upgrade', upgradeThresholds.nights)}
            </>,
            { gridColumn: { md: '1 / -1' } },
          )}
      </Box>

      <LuxLoyaltyChangeTierModal
        open={changeTierModalOpen}
        onClose={() => setChangeTierModalOpen(false)}
        customerId={account.customerId}
        currentTier={account.tier}
        onSuccess={() => onAccountUpdated?.()}
      />
      {canAdjustPoints && (
        <LuxLoyaltyAdjustPointsModal
          open={adjustPointsModalOpen}
          onClose={() => setAdjustPointsModalOpen(false)}
          account={account}
          onSuccess={() => onAccountUpdated?.()}
        />
      )}
    </Stack>
  );
}
